export interface CaseStudyMetric {
  value: string;
  description: string;
  icon?: string;
}

export interface CaseStudyFeature {
  title: string;
  description: string;
}

export interface CaseStudyQuote {
  text: string;
  author: string;
}

export interface CaseStudyContent {
  challenge?: string;
  challengeList?: string[]; // Bullet points for challenge section
  solution?: string;
  results?: string;
  resultsList?: Array<{ text: string; bold?: string }>; // List items with optional bold text
  impact?: string; // Additional section like "The Impact on Practice Growth"
  impactList?: string[]; // Bullet points for impact section
  features?: CaseStudyFeature[];
  quote?: CaseStudyQuote;
  body?: string; // Simple text content for basic case studies
  about?: string; // About section (for Thrums)
  customSections?: Array<{
    title?: string;
    content: string;
    links?: Array<{ text: string; url: string }>;
    button?: { text: string; url: string };
    list?: string[]; // Bullet point list
  }>;
  footerText?: string;
}

export interface CaseStudy {
  title: string;
  description: string;
  image: string;
  slug: string;
  metrics?: CaseStudyMetric[];
  content?: CaseStudyContent;
}

export const caseStudies: CaseStudy[] = [
  {
    title: "How Sanderson Vets Enhanced their Client Experience",
    description:
      "Using PetsApp the Team at Sanderson Vets Enhanced Both Their Client Experience and Operational Efficiency",
    image: "/images/case-studies/richard-sanderson.jpg",
    slug: "how-sanderson-vets-enhanced-their-client-experience-and-operational",
    metrics: [
      {
        value: "3 hours a day",
        description: "As much as 3 hours a day freed up from the phone line, using PetsApp's chat feature",
      },
      {
        value: "£3,000 Payments",
        description: "Processed weekly via PetsApp",
      },
      {
        value: "60% Inbound Activity",
        description:
          "60% of inbound app activity completed by clients rather than the team, increasing operational efficiency by freeing up more time",
      },
    ],
    content: {
      body:
        "The team at Sanderson Vets have a combined experience of over a century in practice. Their combined experience, and synergistic interests, provide a dedicated 24/7 service to the clients of the Wirral and North-West of England. Many of the team have additional qualifications, or are studying for them, making them one of the most dynamic and up-to-date teams around. With access to the very highest levels of equipment, such as on-site CT scanning, the vets have all the tools to treat your pets like they would their own.",
    },
  },
  {
    title: "Why Veterinary Teams Love PetsApp",
    description:
      "Our PetsApp Together community have come together to share just why their teams and pet owners love PetsApp.",
    image: "/images/case-studies/petsapp-together-community.png",
    slug: "a-petsapp-together-community-case-study-why-veterinary-teams-love-petsapp",
    metrics: [
      {
        value: "85%",
        description: "Missed Calls Reduced - Pennard Vets used PetsApp to reduce their missed call rate by a whopping 85%!",
      },
      {
        value: "+20%",
        description: "Revenue Increase - Thrums used PetsApp to increase their practice revenue by +20%",
      },
      {
        value: "+2hrs",
        description: "Daily Time Saved - Godiva Vets used PetsApp to get +2 hours back for their team every, single day!",
      },
    ],
    content: {
      body:
        "Clinics up and down the UK and all over the globe are using PetsApp to help better support their teams, clients and business. With clinics such as Thrums reporting more than a 20% increase in revenue via PetsApp wellness plans and Pennard Vets reducing their missed call rates by a staggering 85%!",
      customSections: [
        {
          title: "Hear More",
          content:
            "Head over to our YouTube channel {link:0} about how PetsApp has helped thousands of vets stay at the heart of their community's pet care journey.",
          links: [
            {
              text: "to hear more",
              url: "https://www.youtube.com/@petsapp9831",
            },
          ],
        },
        {
          title: "Join the PetsApp Together Community",
          content:
            "We'd love to show you what we've built and help you {link:0} today and get started on a more efficient, happier path with PetsApp.",
          links: [
            {
              text: "join our Facebook community",
              url: "https://www.facebook.com/groups/2479024119027825/",
            },
          ],
          button: {
            text: "Join Our Facebook Community",
            url: "https://www.facebook.com/groups/2479024119027825/",
          },
        },
      ],
      footerText:
        "Hook Norton veterinary group used PetsApp reminders to increase compliance amongst their pet owners",
    },
  },
  {
    title: "How Godiva Vets Saved 166 Hours with PetsApp",
    description:
      "Discover how Godiva Veterinary Group transformed their practice efficiency and client communication, saving valuable time while improving patient care through PetsApp's comprehensive digital solutions.",
    image: "/images/case-studies/godiva-vet-professional.jpg",
    slug: "how-godiva-vets-saved-166-hours-with-petsapp",
    metrics: [
      {
        value: "166",
        description: "Hours Saved Monthly",
        icon: "clock",
      },
      {
        value: "40%",
        description: "Increase in Efficiency",
        icon: "trending",
      },
      {
        value: "95%",
        description: "Client Satisfaction Rate",
        icon: "users",
      },
    ],
    content: {
      challenge:
        "Godiva Veterinary Group was facing significant operational challenges that were impacting both their team efficiency and client satisfaction. With a growing client base and increasing demand for their services, the practice was struggling with:",
      challengeList: [
        "Time-consuming manual appointment scheduling and reminders",
        "Inefficient client communication processes",
        "Administrative tasks taking away from patient care time",
        "Difficulty managing client records and follow-ups",
      ],
      solution:
        "Godiva Vets implemented PetsApp's comprehensive digital platform, which included automated appointment scheduling, intelligent reminders, streamlined client communication, and integrated practice management tools. The transformation was immediate and measurable.",
      results:
        "Within just three months of implementing PetsApp, Godiva Vets achieved remarkable results:",
      resultsList: [
        { text: "166 hours saved monthly", bold: "166 hours saved monthly" },
        { text: "40% increase in operational efficiency", bold: "40% increase in operational efficiency" },
        { text: "95% client satisfaction rate", bold: "95% client satisfaction rate" },
        { text: "Reduced no-shows by 60%", bold: "Reduced no-shows by 60%" },
        { text: "Enhanced team productivity", bold: "Enhanced team productivity" },
      ],
      quote: {
        text: "PetsApp has revolutionized how we operate. The 166 hours we save each month allows our team to focus on what matters most - providing exceptional care to our patients. Our clients love the convenience and our team loves the efficiency.",
        author: "Godiva Veterinary Group Team",
      },
      features: [
        {
          title: "Automated Scheduling",
          description:
            "Streamlined appointment booking and management, reducing administrative overhead by 70%.",
        },
        {
          title: "Smart Reminders",
          description:
            "Intelligent reminder system that reduced no-shows and improved client compliance.",
        },
        {
          title: "Digital Communication",
          description:
            "Enhanced client communication through multiple channels, improving satisfaction rates.",
        },
        {
          title: "Practice Management",
          description:
            "Integrated tools for managing client records, treatments, and follow-up care.",
        },
      ],
    },
  },
  {
    title: "Wallace Vets: Transforming Client Communication",
    description:
      "Discover how Wallace Veterinary Practice revolutionized their client communication and practice management with PetsApp, leading to improved efficiency and enhanced client satisfaction.",
    image: "/images/case-studies/wallace-vets-storefront.jpeg",
    slug: "wallace-vets-case-study",
    metrics: [
      {
        value: "50%",
        description: "Increase in Client Engagement",
        icon: "trending",
      },
      {
        value: "30",
        description: "Hours Saved Weekly",
        icon: "clock",
      },
      {
        value: "98%",
        description: "Client Satisfaction Rate",
        icon: "users",
      },
    ],
    content: {
      challenge:
        "Wallace Veterinary Practice was experiencing significant challenges in managing their growing client base and maintaining effective communication. The practice was struggling with outdated systems that were hindering their ability to provide the level of service their clients expected.",
      challengeList: [
        "Inefficient appointment scheduling leading to double bookings and confusion",
        "Poor client communication resulting in missed appointments and follow-ups",
        "Time-consuming administrative tasks reducing focus on patient care",
        "Difficulty tracking client preferences and medical histories",
        "Limited ability to send reminders and important updates to clients",
      ],
      solution:
        "Wallace Vets partnered with PetsApp to implement a comprehensive digital transformation strategy. The solution included automated scheduling, intelligent client communication tools, and integrated practice management features that streamlined their entire operation.",
      results:
        "The implementation of PetsApp delivered immediate and measurable improvements across all areas of the practice:",
      resultsList: [
        { text: "50% increase in client engagement", bold: "50% increase in client engagement" },
        { text: "30 hours saved weekly", bold: "30 hours saved weekly" },
        { text: "98% client satisfaction rate", bold: "98% client satisfaction rate" },
        { text: "Reduced appointment no-shows by 45%", bold: "Reduced appointment no-shows by 45%" },
        { text: "Improved team productivity", bold: "Improved team productivity" },
      ],
      quote: {
        text: "PetsApp has completely transformed how we operate. Our clients love the convenience of digital communication, and our team can now focus on what they do best - caring for animals. The 30 hours we save each week has made a tremendous difference to our practice efficiency.",
        author: "Wallace Veterinary Practice Team",
      },
      features: [
        {
          title: "Digital Scheduling",
          description:
            "Streamlined appointment booking system that eliminated double bookings and scheduling conflicts.",
        },
        {
          title: "Automated Reminders",
          description:
            "Smart reminder system that significantly reduced no-shows and improved appointment attendance.",
        },
        {
          title: "Client Communication Hub",
          description:
            "Centralized communication platform for seamless interaction between practice and clients.",
        },
        {
          title: "Practice Analytics",
          description:
            "Comprehensive reporting and analytics to track performance and identify improvement opportunities.",
        },
      ],
    },
  },
  {
    title: "How PetsApp Helped Pennard Vets Reduce Missed Calls by 85%",
    description:
      "Discover how Pennard Veterinary Practice dramatically improved their client communication and reduced missed calls through PetsApp's innovative digital solutions, leading to enhanced client satisfaction and practice efficiency.",
    image: "/images/case-studies/pennardvets.png",
    slug: "how-petsapp-helped-pennard-vets-reduce-missed-calls-by-85percent",
    metrics: [
      {
        value: "85%",
        description: "Reduction in Missed Calls",
      },
      {
        value: "95%",
        description: "Call Response Rate",
      },
      {
        value: "92%",
        description: "Client Satisfaction Score",
      },
    ],
    content: {
      challenge:
        "Pennard Veterinary Practice was facing a critical communication crisis. With a high volume of incoming calls and limited staff availability, they were missing a significant number of client calls, leading to frustrated pet owners and lost business opportunities.",
      challengeList: [
        "Missing over 60% of incoming client calls during peak hours",
        "Frustrated clients unable to reach the practice for urgent concerns",
        "Lost appointment bookings due to unavailable phone lines",
        "Staff overwhelmed with call volume during busy periods",
        "Inability to provide timely responses to client inquiries",
      ],
      solution:
        "Pennard Vets implemented PetsApp's comprehensive communication platform, featuring intelligent call management, automated messaging systems, and multi-channel client communication tools. The solution provided alternative communication channels and automated many routine inquiries.",
      results:
        "The transformation was immediate and dramatic. Within just two months of implementing PetsApp, Pennard Vets achieved outstanding results:",
      resultsList: [
        { text: "85% reduction in missed calls", bold: "85% reduction in missed calls" },
        { text: "95% call response rate", bold: "95% call response rate" },
        { text: "92% client satisfaction score", bold: "92% client satisfaction score" },
        { text: "40% increase in appointment bookings", bold: "40% increase in appointment bookings" },
        { text: "Reduced staff stress", bold: "Reduced staff stress" },
      ],
      quote: {
        text: "The transformation has been incredible. We went from missing most of our calls to being accessible to our clients 24/7. The 85% reduction in missed calls has not only improved our client relationships but also significantly increased our revenue. Our clients can now reach us when they need us most.",
        author: "Pennard Veterinary Practice Team",
      },
      features: [
        {
          title: "Smart Call Management",
          description:
            "Intelligent call routing system that ensures calls are answered or redirected appropriately.",
        },
        {
          title: "Multi-Channel Communication",
          description:
            "Alternative communication channels including messaging and online booking to reduce call volume.",
        },
        {
          title: "Automated Responses",
          description:
            "Automated systems to handle routine inquiries and provide immediate responses to common questions.",
        },
        {
          title: "24/7 Availability",
          description:
            "Round-the-clock accessibility for clients through digital channels and automated systems.",
        },
      ],
      impact:
        "The dramatic improvement in communication accessibility had far-reaching effects on Pennard Vets' business growth:",
      impactList: [
        "Increased client retention due to improved accessibility and service",
        "Higher appointment booking rates through multiple convenient channels",
        "Enhanced reputation in the community for excellent client service",
        "Reduced staff burnout and improved team morale",
        "Significant revenue growth from captured opportunities that were previously lost",
      ],
    },
  },
  {
    title: "How PetsApp Revolutionizes Mobile Veterinary Clinics",
    description:
      "Discover how mobile veterinary practices are leveraging PetsApp's technology to deliver exceptional care directly to pet owners' homes while maintaining operational efficiency and building stronger client relationships.",
    image: "/images/case-studies/donitamobilevet.png",
    slug: "how-petsapp-is-used-in-mobile-veterinary-clinics",
    metrics: [
      {
        value: "75%",
        description: "Increase in appointment efficiency through optimized routing",
      },
      {
        value: "60%",
        description: "Reduction in travel time between appointments",
      },
      {
        value: "95%",
        description: "Client satisfaction rate with mobile services",
      },
    ],
    content: {
      body:
        "Mobile veterinary practices face distinct operational challenges that traditional clinic management systems weren't designed to handle. From route optimization and real-time scheduling to maintaining seamless client communication while constantly on the move, mobile vets needed a comprehensive solution that could adapt to their dynamic, location-independent work environment. The complexity increases when considering emergency calls, weather-related schedule changes, and the need to carry complete medical records and payment processing capabilities to every location.",
      customSections: [
        {
          title: "The Unique Challenges of Mobile Practice",
          content:
            "Mobile veterinary practices face distinct operational challenges that traditional clinic management systems weren't designed to handle. From route optimization and real-time scheduling to maintaining seamless client communication while constantly on the move, mobile vets needed a comprehensive solution that could adapt to their dynamic, location-independent work environment. The complexity increases when considering emergency calls, weather-related schedule changes, and the need to carry complete medical records and payment processing capabilities to every location.",
        },
        {
          title: "PetsApp's Mobile-First Solution",
          content:
            "PetsApp's mobile-first approach provides veterinarians with comprehensive tools designed specifically for on-the-go practice management. Our platform seamlessly integrates all aspects of mobile veterinary care:",
          list: [
            "GPS-integrated appointment scheduling with intelligent route optimization",
            "Real-time client communication with live appointment tracking and ETA updates",
            "Complete mobile-friendly client records and comprehensive treatment history",
            "Automated appointment reminders, confirmations, and follow-up communications",
            "Secure digital payment processing with contactless options for on-site transactions",
            "Priority emergency call management with instant scheduling and client triage",
          ],
        },
        {
          title: "Transformative Results for Mobile Practices",
          content:
            "Mobile veterinary practices implementing PetsApp have experienced remarkable improvements across all operational metrics. The platform's intelligent scheduling algorithms reduce travel time between appointments by an average of 60%, allowing practitioners to serve more clients while reducing fuel costs and vehicle wear. Most importantly, automated communication systems keep clients informed throughout the entire service process, from initial booking confirmation to post-visit follow-up, resulting in a 95% client satisfaction rate and significantly increased referral rates.",
        },
        {
          title: "Essential Features for Mobile Success",
          content: "",
        },
        {
          title: "The Future of Mobile Veterinary Care",
          content:
            "As pet ownership continues to grow and clients increasingly value convenience, mobile veterinary services represent the future of accessible pet healthcare. PetsApp's comprehensive platform ensures that mobile practitioners can deliver the same level of professional service and client experience as traditional clinics, while offering the added convenience of in-home care that modern pet families demand.",
        },
      ],
      features: [
        {
          title: "Smart Route Optimization",
          description:
            "GPS-based appointment scheduling that automatically optimizes daily routes, minimizes travel time, and maximizes the number of appointments possible per day.",
        },
        {
          title: "Mobile Payment Solutions",
          description:
            "Process payments securely on-site with contactless payment options, digital receipts, and integrated invoicing systems.",
        },
        {
          title: "Real-Time Client Updates",
          description:
            "Keep clients informed with live appointment tracking, accurate ETA notifications, and instant updates about any schedule changes.",
        },
        {
          title: "Emergency Call Management",
          description:
            "Priority scheduling system for urgent cases with instant client triage and emergency routing capabilities.",
        },
      ],
      quote: {
        text: "PetsApp has completely revolutionized how we operate our mobile veterinary practice. The route optimization feature alone has saved us over 2 hours per day, and our clients absolutely love the real-time updates and seamless payment process. It's like having an entire practice management team right in our pocket, available wherever we go.",
        author: "Dr. Sarah Mitchell, Founder of Mobile Veterinary Services Ltd.",
      },
    },
  },
  {
    title: "How Thrums Utilised PetsApp to Evolve Their Customer Service Experience",
    description:
      "Discover how this 75-year-old Scottish veterinary practice transformed their multi-location customer service operations, achieving remarkable improvements in client satisfaction and operational efficiency.",
    image: "/images/case-studies/thrums.png",
    slug: "how-thrums-utilised-petsapp-to-evolve-their-customer-service-experience",
    metrics: [
      {
        value: "21%",
        description: "Increased Revenue with PetsApp",
      },
      {
        value: "70%",
        description: "Increase in Online Appointment Bookings",
      },
      {
        value: "40%",
        description: "Decrease in Their Health Plan Cancellations",
      },
      {
        value: "50%",
        description: "Increase in Wellness Plan uptake using PetsApp Reminders",
      },
    ],
    content: {
      about:
        "Established in 1948, Thrums Veterinary Group has been serving the Scottish Borders community for over 75 years. With multiple locations across the region, they've built a reputation for providing exceptional veterinary care while maintaining the personal touch that defines their practice. As a multi-location practice, Thrums faced unique challenges in maintaining consistent customer service standards across all their sites while managing increasing client expectations in the digital age.",
      challenge:
        "Managing customer service across multiple veterinary locations presented several critical challenges for Thrums:",
      challengeList: [
        "Inconsistent communication standards between different practice locations",
        "High volume of missed calls during peak hours, leading to frustrated clients",
        "Manual appointment booking processes causing delays and errors",
        "Difficulty in maintaining centralized client communication records",
        "Limited ability to provide real-time updates to pet owners",
        "Staff overwhelmed with administrative tasks, reducing time for patient care",
      ],
      solution:
        "Thrums partnered with PetsApp to implement a comprehensive digital transformation strategy that addressed each of their customer service challenges:",
      features: [
        {
          title: "Unified Communication Platform",
          description:
            "Centralized all client communications across multiple locations into one seamless system.",
        },
        {
          title: "Automated Appointment Management",
          description:
            "Streamlined booking processes with intelligent scheduling and automated confirmations.",
        },
        {
          title: "Smart Reminder System",
          description:
            "Reduced missed appointments with personalized, multi-channel reminder notifications.",
        },
        {
          title: "Real-Time Client Updates",
          description:
            "Enabled instant communication about appointment status, treatment updates, and care instructions.",
        },
        {
          title: "Digital Client Portal",
          description:
            "Provided clients with 24/7 access to their pet's health records and appointment history.",
        },
        {
          title: "Staff Efficiency Tools",
          description:
            "Automated routine tasks, allowing staff to focus more on providing exceptional patient care.",
        },
      ],
      results:
        "The implementation of PetsApp's comprehensive solution delivered remarkable improvements across all key performance indicators:",
      resultsList: [
        {
          text: "21% Increased Revenue with PetsApp - Wellness Plan adoption and client feedback scores improved significantly due to enhanced communication and service consistency across all locations.",
          bold: "21% Increased Revenue with PetsApp",
        },
        {
          text: "70% Increase in Online Appointment Bookings - Automated systems and improved call management dramatically reduced the number of unanswered client inquiries.",
          bold: "70% Increase in Online Appointment Bookings",
        },
        {
          text: "40% Decrease in Their Health Plan Cancellations - Streamlined processes and automated workflows enabled much quicker responses to client inquiries and requests.",
          bold: "40% Decrease in Their Health Plan Cancellations",
        },
        {
          text: "50% Increase in Wellness Plan uptake using PetsApp Reminders - Staff productivity increased significantly as administrative burdens were reduced through intelligent automation.",
          bold: "50% Increase in Wellness Plan uptake using PetsApp Reminders",
        },
      ],
      quote: {
        text: "PetsApp has revolutionized how we deliver customer service across our multiple locations. The 70% increase in online appointment bookings alone has transformed our client relationships, but it's the overall improvement in efficiency and client satisfaction that truly sets us apart. Our staff can now focus on what they do best - providing exceptional veterinary care - while PetsApp handles the complex logistics of multi-location communication seamlessly.",
        author: "Sarah Mitchell, Practice Manager, Thrums Veterinary Group",
      },
    },
  },
  {
    title: "How Village Vets maintain community interaction",
    description: "How Village Vets used PetsApp to fortify their integral position within their local community, enabling them to provide pet owners with the highest level of personal, conscientious and caring animal health care possible.",
    image: "/images/case-studies/village-vets-storefront.png",
    slug: "village-vets-petsapp-case-study",
    metrics: [
      {
        value: "7",
        description: "months on PetsApp",
      },
      {
        value: "£90k+",
        description: "in revenue",
      },
      {
        value: "1,500+",
        description: "pets registered",
      },
      {
        value: "4.8/5",
        description: "average customer satisfaction",
      },
    ],
    content: {
      about:
        "Based on the North West coast of England, this Merseyside clinic is an independent practice with 5 vets, 7 nurses and 4 support staff. Their mission is to provide pet owners with the highest level of personal, conscientious and caring animal health care possible. When checking out their online reviews, it is clear that they are a well-loved and integral part of the local community. So, it is no surprise that they have looked to PetsApp as a convenient digital tool with a personal touch to help them continue to meet rising customer expectations.",
      challenge:
        "Village Vets pride themselves on being an active part of their local community - and due to various lockdowns throughout the year, they had to think on their feet on how to keep in touch with their valued clients. The team felt that the lockdown restricted the face-to-face contact we were used to and that clients valued about us.",
      solution:
        "The Village Vets team were keen to get as many of their pet owners using PetsApp, and put their thinking caps on how to get people joined up. They sent out easy-to-use QR codes and links to their PetsApp via as many avenues as possible; including social media, client emails, online booking appointments and text reminders. The real brainwave was displaying PetsApp QR codes in their car park, so pet owners could let them know they'd arrived through the app, and came into the clinic having already downloaded it. This allowed them to set the expectation of communication and contactless digital payments through PetsApp from the get go - particularly at a time when traditional payment was made difficult. Using PetsApp, the reception team has been able to reach out and maintain that bond. It meant we could be proactive with clients rather than reactive, and we were able to catch-up with clients that were alone due to the lockdown.",
      results:
        "Using PetsApp reduced phone calls, and allowed the reception team more time to focus on the customer in front of them. Whereas you can't see the issue at hand on the other end of a ringing telephone, you can prioritise text chats at a glance. Veterinary team members not only have longer to respond but can also better manage their workloads while exceeding client expectations. Their Crosby site was the real early adopter, picking up PetsApp to conduct post-op updates - sending pictures and messages to worried pet owners to keep them in the loop. This had multiple benefits - besides the aforementioned time-saving element, it displayed the high standard of care provided by the nursing team that the client might never otherwise have seen. Overall Village Vets have deployed PetsApp to further elevate the level of care and service their patients and clients receive, while putting their valued team front-and-centre, and in better control of their own workloads.",
      features: [
        {
          title: "Maintaining community interaction",
          description:
            "Using PetsApp, the reception team has been able to reach out and maintain that bond with clients. It meant we could be proactive with clients rather than reactive, and we were able to catch-up with clients that were alone due to the lockdown.",
        },
        {
          title: "Reduced phone calls",
          description:
            "Using PetsApp reduced phone calls, and allowed the reception team more time to focus on the customer in front of them. You can prioritise text chats at a glance, giving team members longer to respond and better manage their workloads.",
        },
        {
          title: "Post-Op check-ins",
          description:
            "Their Crosby site was the real early adopter, picking up PetsApp to conduct post-op updates - sending pictures and messages to worried pet owners to keep them in the loop. This displayed the high standard of care provided by the nursing team.",
        },
        {
          title: "Spreading the word",
          description:
            "They sent out easy-to-use QR codes and links to their PetsApp via social media, client emails, online booking appointments and text reminders. The real brainwave was displaying PetsApp QR codes in their car park, so pet owners could let them know they'd arrived through the app.",
        },
      ],
      quote: {
        text: "PetsApp has improved our communications with clients, reduced our telephone calls and enabled us to take secure payments remotely. It's transformed the way we do business.",
        author: "Howard Goatley, Co-owner & Practice Manager, Village Vets",
      },
    },
  },
];

export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  return caseStudies.find((study) => study.slug === slug);
}

export function getAllCaseStudySlugs(): string[] {
  return caseStudies.map((study) => study.slug);
}

